import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { categoryAPI, evaluationAPI, evaluationTypeAPI, projectAPI } from '../../api/services';
import { Category, CategoryScore, Evaluation, User, Project, TaggedProject } from '../../types';

const EvaluationForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [evaluationPeriod, setEvaluationPeriod] = useState('');
  const [categoryScores, setCategoryScores] = useState<CategoryScore[]>([]);
  const [existingEvaluation, setExistingEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [assignmentId, setAssignmentId] = useState<string | null>(null);
  const [evaluationTypeId, setEvaluationTypeId] = useState<string | null>(null);
  const [myProjects, setMyProjects] = useState<Project[]>([]);
  const [taggedProjects, setTaggedProjects] = useState<TaggedProject[]>([]);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  const levelGroup = ['L0', 'L1', 'L2'].includes(user?.level || '') ? 'L0-L2' : 'L3-L5';

  useEffect(() => {
    // Check for URL parameters (from review assignment)
    const assignmentIdParam = searchParams.get('assignmentId');
    const periodParam = searchParams.get('period');
    const typeIdParam = searchParams.get('typeId');

    if (assignmentIdParam) {
      setAssignmentId(assignmentIdParam);
    }

    if (typeIdParam) {
      setEvaluationTypeId(typeIdParam);
    }

    if (periodParam) {
      setEvaluationPeriod(periodParam);
    }

    // First fetch existing evaluation, then fetch categories based on result
    fetchExistingEvaluation(typeIdParam);
    fetchMyProjects();
  }, [searchParams]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Synchronize categoryScores with categories whenever categories change
  useEffect(() => {
    if (categories.length > 0 && categoryScores.length > 0 && categoryScores.length !== categories.length) {
      console.log('[EVAL FORM] Auto-synchronizing categoryScores due to categories change');
      
      const synchronizedScores: CategoryScore[] = categories.map((category: Category) => {
        // Find existing score for this category by name (not index)
        const existingScore = categoryScores.find(score => (category._id && score.categoryId) ? score.categoryId === category._id : score.categoryName === category.name);
        
        if (existingScore) {
          const updatedScores = category.subCategories.map((sub, subIdx) => {
            const existingSubScore = existingScore.scores.find(s => s.subCategoryName === sub.name);
            return existingSubScore || {
              subCategoryName: sub.name,
              score: 1,
              evidence: '',
            };
          });
          
          return {
            ...existingScore,
            scores: updatedScores,
          };
        }

        return {
          categoryId: category._id,
          categoryName: category.name,
          scores: category.subCategories.map((sub) => ({
            subCategoryName: sub.name,
            score: 1,
            evidence: '',
          })),
          totalWeightedScore: 0,
        };
      });
      
      setCategoryScores(synchronizedScores);
    }
  }, [categories]);

  const fetchMyProjects = async () => {
    try {
      const response = await projectAPI.getMyProjects();
      setMyProjects(response.data.data || []);
    } catch (error) {
      console.error('[EVAL FORM] Error fetching projects:', error);
    }
  };

  const fetchExistingEvaluation = async (urlTypeIdParam?: string | null) => {
    // Get period from URL params if available (from review assignment)
    const periodParam = searchParams.get('period');
    const typeIdParam = urlTypeIdParam || searchParams.get('typeId');
    const evaluationIdParam = searchParams.get('evaluationId');
    const assignmentIdParam = searchParams.get('assignmentId');
    let evaluationLoaded = false;

    try {

      console.log('[EVAL FORM] Fetching evaluation with params:', {
        period: periodParam,
        typeId: typeIdParam,
        evaluationId: evaluationIdParam,
        assignmentId: assignmentIdParam
      });

      let response;

      // If evaluationId is provided, fetch that specific evaluation
      if (evaluationIdParam) {
        console.log('[EVAL FORM] Fetching evaluation by ID:', evaluationIdParam);
        response = await evaluationAPI.getEvaluationById(evaluationIdParam);
      } else {
        // Otherwise, fetch evaluation for specific period if provided, or get most recent
        console.log('[EVAL FORM] Fetching active evaluation by period/type/assignment');
        response = await evaluationAPI.getActiveEvaluation(
          periodParam || undefined,
          typeIdParam || undefined,
          assignmentIdParam || undefined
        );
      }

      console.log('[EVAL FORM] API response:', response.data);

      if (response.data) {
        const evaluation = response.data;
        setExistingEvaluation(evaluation);
        setEvaluationPeriod(evaluation.evaluationPeriod);

        // Ensure scores are numbers and recalculate weighted scores
        const normalizedScores = evaluation.categoryScores.map((cat: CategoryScore) => ({
          ...cat,
          scores: cat.scores.map(s => ({
            ...s,
            score: typeof s.score === 'number' ? s.score : (s.score ? parseFloat(String(s.score)) : 1),
            adminScore: s.adminScore && typeof s.adminScore !== 'number'
              ? parseFloat(String(s.adminScore))
              : s.adminScore,
          })),
          totalWeightedScore: typeof cat.totalWeightedScore === 'number'
            ? cat.totalWeightedScore
            : (cat.totalWeightedScore ? parseFloat(String(cat.totalWeightedScore)) : 0),
        }));

        // Use URL typeId param if available (from assignment), otherwise fall back to evaluation's type
        if (!typeIdParam && evaluation.evaluationTypeId) {
          const evalTypeId = typeof evaluation.evaluationTypeId === 'string'
            ? evaluation.evaluationTypeId
            : evaluation.evaluationTypeId._id || evaluation.evaluationTypeId;

          console.log('[EVAL FORM] Setting evaluationTypeId from evaluation (no URL param):', evalTypeId);
          setEvaluationTypeId(evalTypeId);
        }

        // Check if evaluation is locked (reviewed by admin or assignment closed)
        // const isLocked = evaluation.adminReviewedAt || evaluation.status === 'submitted';
        const isLocked = false
        if (isLocked) {
          console.log('[EVAL FORM] Evaluation is locked (admin reviewed or submitted)');
        }

        setCategoryScores(normalizedScores);

        // Load tagged projects if available
        if (evaluation.taggedProjects && evaluation.taggedProjects.length > 0) {
          setTaggedProjects(evaluation.taggedProjects);
        }

        console.log(`[EVAL FORM] ✓ Loaded existing evaluation for period: ${evaluation.evaluationPeriod}`);
        console.log(`[EVAL FORM] Scores loaded:`, normalizedScores.length, 'categories');
        evaluationLoaded = true;
      } else {
        // No existing evaluation found
        setExistingEvaluation(null);

        // Use period from params or generate default
        const defaultPeriod = periodParam || (() => {
          const now = new Date();
          const quarter = Math.floor(now.getMonth() / 3) + 1;
          return `Q${quarter} ${now.getFullYear()}`;
        })();

        setEvaluationPeriod(defaultPeriod);
        console.log(`[EVAL FORM] No existing evaluation, starting new one for period: ${defaultPeriod}`);
      }
    } catch (error) {
      console.error('[EVAL FORM] Error fetching existing evaluation:', error);
      setExistingEvaluation(null);

      // Set default evaluation period on error
      const periodParam = searchParams.get('period');
      const defaultPeriod = periodParam || (() => {
        const now = new Date();
        const quarter = Math.floor(now.getMonth() / 3) + 1;
        return `Q${quarter} ${now.getFullYear()}`;
      })();

      setEvaluationPeriod(defaultPeriod);
    }
    
    // Only fetch categories if no existing evaluation was loaded
    if (!evaluationLoaded) {
      console.log('[EVAL FORM] No existing evaluation found, fetching categories for new evaluation');
      await fetchCategories(typeIdParam);
    } else {
      console.log('[EVAL FORM] Existing evaluation loaded, fetching categories just for UI display');
      // Still need categories state for UI display, but don't reinitialize scores
      await fetchCategoriesForDisplay(typeIdParam);
    }
    setLoading(false);
  };


  const fetchCategoriesAndReorderScores = async (typeId: string | null = null, scores?: CategoryScore[]) => {
    try {
      // Use typeId if available (from assignment), otherwise fetch all by level group
      let response;
      let categoriesData: Category[];

      if (typeId || evaluationTypeId) {
        response = await evaluationTypeAPI.getCategoriesByEvaluationType(
          typeId || evaluationTypeId!,
          levelGroup
        );
        // This API returns { data: categories }
        categoriesData = response.data.data || [];
      } else {
        response = await categoryAPI.getCategoriesByLevelGroup(levelGroup);
        // This API returns categories directly
        categoriesData = response.data || [];
      }

      // Ensure we have an array
      if (!Array.isArray(categoriesData)) {
        console.error('Categories data is not an array:', categoriesData);
        categoriesData = [];
      }

      // Reorder categoryScores to match categories order BEFORE setting categories
      const scoresToReorder = scores || categoryScores;
      let finalScores = scoresToReorder;
      
      if (scoresToReorder.length > 0) {
        // Reorder existing scores to match category order
        const reorderedScores = categoriesData.map(category => {
          const existingScore = scoresToReorder.find(score => (category._id && score.categoryId) ? score.categoryId === category._id : score.categoryName === category.name);
          if (existingScore) {
            return existingScore;
          }
          // Create new category score for newly added categories
          return {
            categoryName: category.name,
            scores: category.subCategories.map((sub) => ({
              subCategoryName: sub.name,
              score: 1,
              evidence: '',
            })),
            totalWeightedScore: 0,
          };
        });
        finalScores = reorderedScores;
      } else if (categoriesData.length > 0 && !scores) {
        // Initialize scores ONLY if no scores exist AND no scores were passed in
        console.log('[EVAL FORM] Initializing new category scores');
        finalScores = categoriesData.map((category: Category) => ({
          categoryId: category._id,
          categoryName: category.name,
          scores: category.subCategories.map((sub) => ({
            subCategoryName: sub.name,
            score: 1,
            evidence: '',
          })),
          totalWeightedScore: 0,
        }));
      }

      // Set both states together to prevent mismatch
      setCategories(categoriesData);
      setCategoryScores(finalScores);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]); // Set empty array on error to prevent crashes
    }
  };

  const fetchCategories = async (typeId: string | null = null) => {
    await fetchCategoriesAndReorderScores(typeId);
  };

  const fetchCategoriesForDisplay = async (typeId: string | null = null) => {
    try {
      // Use typeId if available (from assignment), otherwise fetch all by level group
      let response;
      let categoriesData: Category[];

      if (typeId || evaluationTypeId) {
        response = await evaluationTypeAPI.getCategoriesByEvaluationType(
          typeId || evaluationTypeId!,
          levelGroup
        );
        categoriesData = response.data.data || [];
      } else {
        response = await categoryAPI.getCategoriesByLevelGroup(levelGroup);
        categoriesData = response.data || [];
      }

      // Ensure we have an array
      if (!Array.isArray(categoriesData)) {
        console.error('Categories data is not an array:', categoriesData);
        categoriesData = [];
      }

      // Only set categories, don't touch scores
      setCategories(categoriesData);
      console.log('[EVAL FORM] Categories loaded for display only (no score initialization)');
    } catch (error) {
      console.error('Error fetching categories for display:', error);
      setCategories([]);
    }
  };


  const handleScoreChange = (categoryIndex: number, subIndex: number, score: number) => {
    console.log('[EVAL FORM] handleScoreChange called:', {
      categoryIndex,
      subIndex, 
      score,
      categoriesLength: categories.length,
      categoryScoresLength: categoryScores.length,
      categoryScoresStructure: categoryScores.map((cs, i) => ({ 
        index: i, 
        name: cs.categoryName, 
        scoresCount: cs.scores?.length 
      }))
    });

    // Check if categoryScores needs to be synchronized with categories
    if (categories.length > 0 && (categoryScores.length === 0 || categoryScores.length !== categories.length)) {
      console.log('[EVAL FORM] Synchronizing categoryScores with categories');
      
      // Create a new synchronized array
      const synchronizedScores: CategoryScore[] = categories.map((category: Category) => {
        // Find existing score for this category by name (not index)
        const existingScore = categoryScores.find(score => (category._id && score.categoryId) ? score.categoryId === category._id : score.categoryName === category.name);
        
        if (existingScore) {
          // Make sure all subcategories exist
          const updatedScores = category.subCategories.map((sub) => {
            const existingSubScore = existingScore.scores.find(s => s.subCategoryName === sub.name);
            return existingSubScore || {
              subCategoryName: sub.name,
              score: 1,
              evidence: '',
            };
          });
          
          return {
            ...existingScore,
            scores: updatedScores,
          };
        }
        
        // Create new category score for newly added categories
        return {
          categoryId: category._id,
          categoryName: category.name,
          scores: category.subCategories.map((sub) => ({
            subCategoryName: sub.name,
            score: 1,
            evidence: '',
          })),
          totalWeightedScore: 0,
        };
      });
      
      setCategoryScores(synchronizedScores);
      return; // Return and let the next render handle the score change
    }

    const newCategoryScores = [...categoryScores];

    // Guard clause: ensure the category and subcategory exist
    if (!newCategoryScores[categoryIndex]) {
      console.error('[EVAL FORM] Invalid category index', { 
        categoryIndex,
        categoryScoresLength: newCategoryScores.length
      });
      return;
    }

    if (!newCategoryScores[categoryIndex].scores[subIndex]) {
      console.error('[EVAL FORM] Invalid subcategory index', { 
        categoryIndex, 
        subIndex,
        scoresLength: newCategoryScores[categoryIndex]?.scores?.length
      });
      return;
    }

    newCategoryScores[categoryIndex].scores[subIndex].score = score;

    // Calculate weighted score for this category
    const category = categories[categoryIndex];

    // Guard clause: ensure category exists
    if (!category || !category.subCategories) {
      console.error('[EVAL FORM] Category not found or missing subCategories', { categoryIndex });
      return;
    }

    let totalWeighted = 0;
    category.subCategories.forEach((sub, idx) => {
      const subScore = newCategoryScores[categoryIndex].scores[idx]?.score || 0;
      const weight = sub.weight ?? 0;

      // Ensure both are numbers before multiplication
      const numericScore = typeof subScore === 'number' ? subScore : parseFloat(String(subScore)) || 0;
      const numericWeight = typeof weight === 'number' ? weight : parseFloat(String(weight)) || 0;

      totalWeighted += numericScore * numericWeight;
    });

    // Ensure totalWeighted is a proper number
    newCategoryScores[categoryIndex].totalWeightedScore = isNaN(totalWeighted) ? 0 : totalWeighted;

    setCategoryScores(newCategoryScores);
    debouncedAutoSave();
  };

  const handleEvidenceChange = (categoryIndex: number, subIndex: number, evidence: string) => {
    // Early return if data isn't ready
    if (categories.length === 0 || categoryScores.length === 0) {
      console.log('[EVAL FORM] Skipping evidence change - data not ready');
      return;
    }

    if (categoryScores.length !== categories.length) {
      console.log('[EVAL FORM] Evidence change triggered categoryScores synchronization');
      // Force a re-render with synchronized data, then the user can try the evidence change again
      handleScoreChange(categoryIndex, subIndex, categoryScores[categoryIndex]?.scores[subIndex]?.score || 1);
      return;
    }

    const newCategoryScores = [...categoryScores];

    // Guard clause: ensure the category and subcategory exist
    if (!newCategoryScores[categoryIndex]) {
      console.error('[EVAL FORM] Invalid category index', { categoryIndex });
      return;
    }

    if (!newCategoryScores[categoryIndex].scores[subIndex]) {
      console.error('[EVAL FORM] Invalid subcategory index', { categoryIndex, subIndex });
      return;
    }

    newCategoryScores[categoryIndex].scores[subIndex].evidence = evidence;
    setCategoryScores(newCategoryScores);
    debouncedAutoSave();
  };

  const handleAddProject = (project: Project) => {
    // Check if project is already tagged
    if (taggedProjects.some(tp => tp.projectId === project._id)) {
      return;
    }

    const newTaggedProject: TaggedProject = {
      projectId: project._id,
      projectName: project.name,
      relevance: '',
      contribution: '',
    };

    setTaggedProjects([...taggedProjects, newTaggedProject]);
    setShowProjectSelector(false);
  };

  const handleRemoveProject = (projectId: string) => {
    setTaggedProjects(taggedProjects.filter(tp => tp.projectId !== projectId));
  };

  const handleProjectMetadataChange = (projectId: string, field: 'relevance' | 'contribution', value: string) => {
    setTaggedProjects(taggedProjects.map(tp =>
      tp.projectId === projectId ? { ...tp, [field]: value } : tp
    ));
    debouncedAutoSave();
  };

  const calculateTotalScore = () => {
    const total = categoryScores.reduce((sum, cat) => {
      const weightedScore = typeof cat.totalWeightedScore === 'number'
        ? cat.totalWeightedScore
        : parseFloat(String(cat.totalWeightedScore)) || 0;
      return sum + (isNaN(weightedScore) ? 0 : weightedScore);
    }, 0);
    
    // Warn if total exceeds expected maximum (5.0)
    if (total > 5.0) {
      console.warn('[EVAL FORM] Total score exceeds maximum:', {
        total: total.toFixed(4),
        categoryBreakdown: categoryScores.map((cat, idx) => ({
          category: cat.categoryName,
          weightedScore: cat.totalWeightedScore,
          categoryWeight: categories[idx]?.weight || 'unknown'
        }))
      });
    }
    
    return total;
  };

  const validateScores = () => {
    const total = calculateTotalScore();
    const issues = [];
    
    // Check if total score is reasonable (shouldn't exceed 5.0 by much)
    if (total > 5.1) {
      issues.push(`Total score (${total.toFixed(2)}) significantly exceeds maximum (5.0). This may indicate weight misconfiguration.`);
    }
    
    // Check individual category weights
    const totalCategoryWeight = categories.reduce((sum, cat) => sum + (cat.weight || 0), 0);
    if (Math.abs(totalCategoryWeight - 1.0) > 0.01) {
      issues.push(`Category weights sum to ${(totalCategoryWeight * 100).toFixed(1)}% instead of 100%. Weights may need recalibration.`);
    }
    
    return {
      isValid: issues.length === 0,
      issues,
      total
    };
  };

  const autoSave = useCallback(async () => {
    if (categoryScores.length === 0) return;

    // Always read from URL params for assignment context — state may be stale
    const urlTypeId = searchParams.get('typeId');
    const urlAssignmentId = searchParams.get('assignmentId');

    setIsSaving(true);
    try {
      const evaluationData: any = {
        evaluationPeriod,
        categoryScores,
        taggedProjects,
      };

      evaluationData.assignmentId = urlAssignmentId || assignmentId || undefined;
      evaluationData.evaluationTypeId = urlTypeId || evaluationTypeId || undefined;

      await evaluationAPI.createOrUpdateEvaluation(evaluationData);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  }, [categoryScores, taggedProjects, evaluationPeriod, assignmentId, evaluationTypeId, searchParams]);

  const debouncedAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave();
    }, 2000); // Auto-save after 2 seconds of no changes
  }, [autoSave]);

  const handleSubmit = async () => {
    setShowConfirmDialog(true);
  };

  const confirmSubmit = async () => {
    const validation = validateScores();
    
    if (!validation.isValid) {
      alert(`Cannot submit evaluation due to validation issues:\\n\\n${validation.issues.join('\\n\\n')}\\n\\nPlease contact your administrator to review the category weights configuration.`);
      setShowConfirmDialog(false);
      return;
    }

    setLoading(true);
    setShowConfirmDialog(false);
    try {
      const urlTypeId = searchParams.get('typeId');
      const urlAssignmentId = searchParams.get('assignmentId');

      const evaluationData: any = {
        evaluationPeriod,
        categoryScores,
        taggedProjects,
        assignmentId: urlAssignmentId || assignmentId || undefined,
        evaluationTypeId: urlTypeId || evaluationTypeId || undefined,
      };

      const response = await evaluationAPI.createOrUpdateEvaluation(evaluationData);
      await evaluationAPI.submitEvaluation(response.data.evaluation._id);

      setSuccess(true);
      setTimeout(() => {
        navigate('/employee/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error submitting evaluation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (submit: boolean = false) => {
    // Validate scores before saving
    const validation = validateScores();
    
    if (!validation.isValid && submit) {
      // For submission, show validation errors
      alert(`Cannot submit evaluation due to validation issues:\n\n${validation.issues.join('\n\n')}\n\nPlease contact your administrator to review the category weights configuration.`);
      return;
    }
    
    if (!validation.isValid) {
      // For drafts, warn but allow saving
      console.warn('[EVAL FORM] Saving with validation issues:', validation.issues);
    }

    setLoading(true);
    try {
      const urlTypeId = searchParams.get('typeId');
      const urlAssignmentId = searchParams.get('assignmentId');

      const evaluationData: any = {
        evaluationPeriod,
        categoryScores,
        taggedProjects,
        assignmentId: urlAssignmentId || assignmentId || undefined,
        evaluationTypeId: urlTypeId || evaluationTypeId || undefined,
      };

      const response = await evaluationAPI.createOrUpdateEvaluation(evaluationData);

      if (submit) {
        await evaluationAPI.submitEvaluation(response.data.evaluation._id);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/employee/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error saving evaluation:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalScore = calculateTotalScore();
  const validation = validateScores();

  // Check if evaluation is locked (admin reviewed or submitted)
  const isLocked = existingEvaluation && (
    existingEvaluation.adminReviewedAt ||
    existingEvaluation.status === 'submitted'
  );

  // Check if there's no open review - no assignment and no existing evaluation
  const hasNoOpenReview = !loading && !assignmentId && !existingEvaluation;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[50vh]">
        <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // If no open review, show message
  if (hasNoOpenReview) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-1">No Open Evaluation</p>
          <p className="text-xs text-gray-400">You don't have any open performance evaluation at the moment.</p>
        </div>
      </div>
    );
  }

  // If locked, show read-only message and redirect option
  // if (isLocked && existingEvaluation) {
  //   return (
  //     <div className="min-h-screen bg-gray-100 py-8">
  //       <div className="max-w-6xl mx-auto px-4">
  //         <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
  //           <div className="flex items-start">
  //             <svg className="w-6 h-6 text-amber-600 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
  //               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  //             </svg>
  //             <div>
  //               <h3 className="text-lg font-semibold text-amber-900 mb-1">
  //                 Evaluation Locked
  //               </h3>
  //               <p className="text-amber-800">
  //                 {existingEvaluation.adminReviewedAt
  //                   ? 'This evaluation has been reviewed by an admin and can no longer be edited.'
  //                   : 'This evaluation has been submitted and can no longer be edited.'}
  //               </p>
  //               <button
  //                 onClick={() => navigate(`/employee/evaluation/${existingEvaluation._id}`)}
  //                 className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
  //               >
  //                 View Evaluation
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="bg-white">
      <div className="px-6">
        <div className="py-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Performance Evaluation</h2>
              <p className="text-sm text-gray-400 mt-0.5">{evaluationPeriod || 'Self-evaluation form'}</p>
            </div>
          </div>

          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
              Evaluation submitted successfully! Redirecting...
            </div>
          )}

          {/* Draft Saving Indicator */}
          <div className="mb-4 flex items-center gap-2 text-xs">
            {isSaving ? (
              <><div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /><span className="text-gray-500">Saving...</span></>
            ) : lastSaved ? (
              <><div className="w-2 h-2 bg-green-500 rounded-full" /><span className="text-green-600">Saved {lastSaved.toLocaleTimeString()}</span></>
            ) : (
              <><div className="w-2 h-2 bg-gray-400 rounded-full" /><span className="text-gray-400">Auto-saves as you type</span></>
            )}
          </div>

          {existingEvaluation && (
            <div className="mb-6">
              {existingEvaluation.lastUpdatedBy === 'admin' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This evaluation was last updated by an admin during your
                    review meeting.
                    {existingEvaluation.lastUpdatedByUser && (
                      <span className="ml-1">
                        Updated on{' '}
                        {new Date(existingEvaluation.updatedAt).toLocaleString()}
                      </span>
                    )}
                  </p>
                </div>
              )}
              {existingEvaluation.status === 'submitted' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Status:</strong> This evaluation has been submitted. You can still edit
                    it if needed.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Info row */}
          <div className="flex items-center gap-4 mb-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center">{(user?.name || '?')[0]}</div>
              <span className="text-gray-700">{user?.name}</span>
            </div>
            <div className="w-px h-4 bg-gray-200" />
            <span className="text-gray-500">{user?.level} ({levelGroup})</span>
            <div className="w-px h-4 bg-gray-200" />
            <span className="text-gray-500">{evaluationPeriod}</span>
          </div>

          <div className="space-y-4">
            {categories.map((category, catIndex) => (
              <div key={category._id} className="border-b border-gray-100 pb-4">
                {/* Category header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">{category.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{((category.weight ?? 0) * 100).toFixed(0)}%</span>
                  </div>
                  <span className="text-xs text-gray-400">Score: {(categoryScores[catIndex]?.totalWeightedScore ?? 0).toFixed(2)}</span>
                </div>

                {/* Subcategories */}
                <div className="space-y-1.5">
                  {category.subCategories.map((sub, subIndex) => {
                    const score = categoryScores[catIndex]?.scores[subIndex]?.score || 1;
                    const weighted = score * (sub.weight ?? 0);
                    return (
                      <div key={subIndex} className="grid grid-cols-12 gap-2 items-center py-1.5 px-2 hover:bg-gray-50 rounded transition-colors">
                        <div className="col-span-3 text-xs text-gray-700">{sub.name}</div>
                        <div className="col-span-1 text-[10px] text-gray-400">{((sub.weight ?? 0) * 100).toFixed(1)}%</div>
                        <div className="col-span-3 flex items-center gap-2">
                          <input type="range" min="1" max="5" step="1" value={score}
                            onChange={e => handleScoreChange(catIndex, subIndex, Number(e.target.value))}
                            className="flex-1 h-1.5 cursor-pointer accent-blue-600" />
                          <span className="w-5 text-xs font-semibold text-gray-800 text-center">{score}</span>
                        </div>
                        <div className="col-span-1 text-[10px] text-gray-400 text-right">{weighted.toFixed(3)}</div>
                        <div className="col-span-4">
                          <input type="text"
                            value={categoryScores[catIndex]?.scores[subIndex]?.evidence || ''}
                            onChange={e => handleEvidenceChange(catIndex, subIndex, e.target.value)}
                            placeholder="Evidence / notes"
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Project Tagging Section */}
          <div className="mt-4 border-b border-gray-100 pb-4">
            <div className="flex justify-between items-center mb-2">
              <div>
                <span className="text-sm font-medium text-gray-800">Attached Projects</span>
                <span className="text-xs text-gray-400 ml-2">Tag projects that showcase your work</span>
              </div>
              <button onClick={() => setShowProjectSelector(!showProjectSelector)}
                className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1">
                <span className="text-sm leading-none">+</span> Add project
              </button>
            </div>

            {/* Project Selector */}
            {showProjectSelector && (() => {
              const [projectSearch, setProjectSearch] = [searchParams.get('_ps') || '', (v: string) => {}];
              // Use local state via a ref-like pattern for search within the render
              return (
                <div className="mb-3">
                  <input placeholder="Search projects..." onChange={e => {
                    // Filter inline via the input value
                    const input = e.target;
                    const list = input.parentElement?.querySelector('[data-project-list]');
                    if (list) {
                      Array.from(list.children).forEach((el: any) => {
                        const name = el.getAttribute('data-name') || '';
                        el.style.display = name.toLowerCase().includes(input.value.toLowerCase()) ? '' : 'none';
                      });
                    }
                  }} className="w-full bg-gray-100 rounded-lg px-3 py-1.5 text-xs mb-2 outline-none focus:ring-2 focus:ring-blue-500" />
                  <div className="max-h-40 overflow-y-auto" data-project-list>
                    {myProjects.length === 0 ? (
                      <p className="text-xs text-gray-400 py-2">No projects found</p>
                    ) : myProjects.map(project => {
                      const isAlreadyTagged = taggedProjects.some(tp => tp.projectId === project._id);
                      const statusColor = project.status === 'done' || project.status === 'production' ? 'bg-green-500' : project.status === 'development' ? 'bg-blue-500' : 'bg-gray-400';
                      return (
                        <div key={project._id} data-name={project.name}
                          onClick={() => !isAlreadyTagged && handleAddProject(project)}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${isAlreadyTagged ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
                          <span className={`w-2 h-2 rounded-full shrink-0 ${statusColor}`} />
                          <span className="text-sm text-gray-700 truncate flex-1 capitalize">{project.name}</span>
                          <span className="text-[10px] text-gray-400 capitalize">{project.status}</span>
                          {isAlreadyTagged && <span className="text-[10px] text-blue-600">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Tagged Projects */}
            {taggedProjects.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">No projects attached yet</p>
            ) : taggedProjects.map(tp => (
              <div key={tp.projectId} className="border-b border-gray-100 py-2">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-sm text-gray-700 capitalize flex-1">{tp.projectName}</span>
                  <button onClick={() => handleRemoveProject(tp.projectId)} className="text-gray-300 hover:text-red-500 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={tp.relevance || ''} onChange={e => handleProjectMetadataChange(tp.projectId, 'relevance', e.target.value)}
                    placeholder="Why is this relevant?" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400" />
                  <input value={tp.contribution || ''} onChange={e => handleProjectMetadataChange(tp.projectId, 'contribution', e.target.value)}
                    placeholder="Your contribution" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400" />
                </div>
              </div>
            ))}
          </div>

          {/* Validation Issues Warning */}
          {/* Validation + Score + Submit — sticky bottom */}
          <div className="mt-4 border-t border-gray-100 pt-4">
            {!validation.isValid && (
              <div className="flex items-start gap-2 mb-3 text-xs text-yellow-700 bg-yellow-50 rounded-lg px-3 py-2">
                <svg className="w-3.5 h-3.5 shrink-0 mt-0.5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                <div>
                  {validation.issues.map((issue: string, i: number) => <span key={i} className="block">{issue}</span>)}
                  <span className="text-[10px] text-yellow-600 mt-1 block">Save as draft is available. Contact admin if this persists.</span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Total Score</span>
                <span className={`text-lg font-bold ${
                  validation.isValid ? 'text-blue-600' : totalScore > 5.0 ? 'text-red-600' : 'text-yellow-600'
                }`}>{(totalScore ?? 0).toFixed(2)}</span>
                <span className="text-xs text-gray-300">/ 5.00</span>
                {totalScore > 5.0 && <span className="text-[10px] text-red-500">Weight misconfiguration</span>}
              </div>
              <button onClick={() => handleSubmit()} disabled={loading || success}
                className="bg-blue-600 text-white py-2 px-5 rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {loading ? 'Submitting...' : 'Submit Evaluation'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="!mt-0 fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-amber-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 13.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Submission</h3>
              </div>
              <p className="text-gray-700 mb-6">
                Are you sure you want to submit your evaluation? Once submitted, you will no longer be able to edit your scores and evidence.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSubmit}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
                >
                  {loading ? 'Submitting...' : 'Yes, Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationForm;
